/**
 * Proposals Service
 * Manages proposal data using BaseService pattern
 */

import { BaseService } from '@/chassis/data/services/base-service';
import { BaseFormService, ValidationRules } from '@/chassis/data/services/base-form-service';
import type { BaseFilters } from '@/chassis/data/services/base-list-service';
import { createSimpleNormalizer } from '@/chassis/data/services/normalizer-utils';
import { DateTimeUtils } from '@/chassis/core/utils';
import { http } from '@/chassis/data/services/http-client';

// ============================================================================
// Interface
// ============================================================================

export interface ProposalsRecord {
    id: string;
    tenderId: string;
    /** Lote ao qual a proposta se refere — 1 proposta = 1 fornecedor + 1 lote */
    lotId: string;
    supplierId: string;
    lotAuctionSessionId?: string;
    proposalNumber: string;
    value: number;
    currency?: string;
    description?: string;
    status?: string;
    submittedAt?: string;
    version?: number;
    previousProposalId?: string;
    envelopeSealed?: boolean;
    envelopeEncryptionKey?: string;
    envelopeHash?: string;
    envelopeSealedAt?: string;
    envelopeOpenedAt?: string;
    envelopeOpenedBy?: string;
    digitalSignature?: string;
    digitalCertificate?: string;
    automaticValidationDone?: boolean;
    validationResult?: string;
    validationReport?: string;
    validatedAt?: string;
    validatedBy?: string;
    feasibilityAnalysisDone?: boolean;
    feasibilityResult?: string;
    percentageOfEstimate?: number;
    referenceValue?: number;
    discountPercentage?: number;
    savingsAmount?: number;
    ranking?: number;
    technicalScore?: number;
    economicScore?: number;
    totalScore?: number;
    judgmentCriteria: string;
    attachments?: string;
    technicalSpecifications?: string;
    qualificationDocuments?: string;
    qualificationStatus?: string;
    qualificationNotes?: string;
    /** Justificativa do status (Lei 14.133 Art. 7° §3°). Adicionado em 2026-04-26
     *  pelo endpoint PATCH /proposals/:id/judgment. Obrigatorio em
     *  PARTIALLY_QUALIFIED e DISQUALIFIED. */
    qualificationReason?: string | null;
    /** Auditoria — quem definiu o status. */
    qualifiedBy?: string;
    /** Auditoria — quando foi definido (ISO timestamp). */
    qualifiedAt?: string;
    deliveryPeriod?: number;
    paymentTerms?: string;
    warrantyPeriod?: number;
    brandModel?: string;
    productOrigin?: string;
    complianceWithRules?: boolean;
    identifiedDeviations?: string;
    appeals?: string;
    clarifications?: string;
    clarificationDeadline?: string;
    comparisonData?: string;
    competitiveAdvantages?: string;
    identifiedDisadvantages?: string;
    riskLevel?: string;
    supplierEvaluation?: string;
    supplierHistory?: string;
    committeeRecommendations?: string;
    generalNotes?: string;
    activityLog?: string;
    proposalExpiryDate?: string;
    isWinner?: boolean;
    winnerDeclaredAt?: string;
    winningReason?: string;
    contractId?: string;
    backupData?: string;
    auditSettings?: string;
    proposalMetadata?: string;
    notificationSettings?: string;
    tags?: string;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    updatedBy?: string;
    active?: boolean;
    /** Items da proposta por lote (preenchido pelo backend ao listar) */
    lotItems?: Array<{
      id?: string;
      itemId?: string;
      unitPrice?: number;
      unitValue?: number;
      brand?: string;
      manufacturer?: string;
      model?: string;
      description?: string;
    }>;
}

export interface ProposalsFilters extends BaseFilters {
    searchText?: string;
    tenderId?: string;
    lotId?: string;
    supplierId?: string;
    status?: string;
    judgmentCriteria?: string;
}

export interface CreateProposalsDto {
    tenderId: string;
    lotId: string;
    supplierId: string;
    proposalNumber: string;
    value: number;
    judgmentCriteria: string;
    lotAuctionSessionId?: string;
    currency?: string;
    description?: string;
    status?: string;
    validationResult?: string;
    qualificationStatus?: string;
    submittedAt?: string;
    version?: number;
    envelopeSealed?: boolean;
    automaticValidationDone?: boolean;
    complianceWithRules?: boolean;
    isWinner?: boolean;
    percentageOfEstimate?: number;
    discountPercentage?: number;
    ranking?: number;
    technicalScore?: number;
    economicScore?: number;
    totalScore?: number;
    deliveryPeriod?: number;
    generalNotes?: string;
    paymentTerms?: string;
    active?: boolean;
}

export interface UpdateProposalsDto extends Partial<CreateProposalsDto> {}

// ============================================================================
// Service Instance
// ============================================================================

const baseNormalizeRecord = createSimpleNormalizer<ProposalsRecord>();

const normalizeRecord = (record: any): ProposalsRecord => {
    const normalized = baseNormalizeRecord(record);

    const resolveBoolean = (value: any): boolean | undefined => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value !== 0;
        return undefined;
    };

    return {
        ...normalized,
        active:
            resolveBoolean(record?.active) ??
            resolveBoolean(normalized.active) ??
            !['rejected'].includes(record?.status),
    };
};

class ProposalsService extends BaseService<ProposalsRecord, CreateProposalsDto, UpdateProposalsDto, ProposalsFilters> {
    constructor() {
        super({
            basePath: '/proposals',
            normalizeRecord,
        });
    }

    // ── Business operations ──────────────────────────────────────────

    /**
     * Fornecedor submete proposta em rascunho.
     * `supplierId` é obrigatório — backend verifica ownership comparando
     * `proposal.supplierId` com `supplierId || userId` do request. Se omitir,
     * cai no fallback `userId` que quase nunca bate com o supplierId
     * (userId=USER:..., supplierId=SUPPLIER:...), resultando em
     * "Apenas o fornecedor dono pode submeter a proposta" (422).
     */
    async submit(proposalId: string, supplierId: string): Promise<ProposalsRecord> {
        const raw = await http.put<ProposalsRecord>(`/proposals/${proposalId}/submit`, { supplierId });
        return normalizeRecord(raw);
    }

    /**
     * Fornecedor retira submissao (volta pra rascunho).
     * `supplierId` obrigatorio — backend compara com proposal.supplierId
     * (prefix-agnostic) e nao tem fallback pra userId.
     */
    /**
     * MAIOR DESCONTO: manda SO o percentual. O servidor aplica o mesmo desconto
     * em cada item do catalogo do lote e grava lotItems/proposalValue.
     *
     * A tela nao calcula de proposito: com orcamento sigiloso ela nem recebe o
     * valor de referencia, e com ele receberia so para refazer uma conta que o
     * servidor tem de refazer de qualquer jeito.
     */
    async applyDiscount(
        proposalId: string,
        discountPercentage: number,
        supplierId?: string,
    ): Promise<{ discountPercentage: number; itemCount: number; referenceTotal: number; proposalValue: number }> {
        const raw = await http.put(`/proposals/${proposalId}/discount`, { discountPercentage, supplierId });
        return ((raw as any)?.data ?? raw) as any;
    }

    async unsubmit(proposalId: string, supplierId: string): Promise<ProposalsRecord> {
        const raw = await http.put<ProposalsRecord>(`/proposals/${proposalId}/unsubmit`, { supplierId });
        return normalizeRecord(raw);
    }

    /** Pregoeiro desclassifica proposta */
    async disqualify(proposalId: string, payload: {
        justification: string;
    }): Promise<ProposalsRecord> {
        const raw = await http.put<ProposalsRecord>(`/proposals/${proposalId}/disqualify`, {
            reason: payload.justification,
        });
        return normalizeRecord(raw);
    }

    /**
     * Pregoeiro RECLASSIFICA proposta DISQUALIFIED -> SUBMITTED (RN-04
     * spec Sessao Publica). Backend valida 'mesmo pregoeiro reverte'
     * (admin bypassa). Justificativa min 20 chars (Art. 7° §3°).
     * @ref Round 3 sprint Sessao Publica.
     */
    async reclassify(proposalId: string, payload: {
        reason: string;
    }): Promise<ProposalsRecord> {
        const raw = await http.put<ProposalsRecord>(`/proposals/${proposalId}/reclassify`, {
            reason: payload.reason,
        });
        return normalizeRecord(raw);
    }

    /**
     * Pregoeiro REVERTE decisao de habilitacao previa (RN-15 spec
     * Sessao Publica). Limpa qualificationStatus/Reason/qualifiedBy/At.
     * Justificativa min 20 chars (Art. 7° §3°).
     * @ref Round 2 sprint Sessao Publica.
     */
    async revertJudgment(proposalId: string, payload: {
        reason: string;
    }): Promise<ProposalsRecord> {
        const raw = await http.patch<ProposalsRecord>(`/proposals/${proposalId}/judgment/revert`, {
            reason: payload.reason,
        });
        return normalizeRecord(raw);
    }

    /**
     * Pregoeiro solicita documentos complementares ao fornecedor durante
     * habilitacao (Art. 64 Lei 14.133). Keyed por proposalId porque Award
     * so existe apos declaracao de vencedor e a diligencia ocorre antes.
     */
    async requestDocuments(proposalId: string, payload: {
        deadline: string;
        documentNames: string[];
        message?: string;
    }): Promise<{ requestId?: string }> {
        const raw = await http.post(`/proposals/${proposalId}/request-documents`, payload);
        const data = (raw as any)?.data ?? raw;
        return { requestId: (data as any)?.requestId };
    }

    /**
     * Pregoeiro/equipe cancela uma solicitação de documentos PENDING
     * (Reunião 28/abr — C2). Backend marca o entry como CANCELLED
     * preservando audit trail. Idempotente. Não cancela FULFILLED.
     */
    async cancelDocumentRequest(
        proposalId: string,
        requestId: string,
        payload?: { reason?: string },
    ): Promise<void> {
        await http.delete(`/proposals/${proposalId}/request-documents/${requestId}`, {
            body: payload ?? {},
        });
    }

    /**
     * Fornecedor anexa documentos em resposta a solicitacao (Art. 64). Keyed
     * por proposalId — award so existe apos declaracao de vencedor.
     */
    async submitRequestResponse(proposalId: string, payload: {
        requestId: string;
        documentIds: string[];
        message?: string;
        supplierId?: string;
    }): Promise<{ respondedAt?: string }> {
        const raw = await http.post(`/proposals/${proposalId}/submit-request-response`, payload);
        const data = (raw as any)?.data ?? raw;
        return { respondedAt: (data as any)?.respondedAt };
    }

    /**
     * Pregoeiro define o status de habilitacao do fornecedor (Lei 14.133
     * Art. 7° §3° + Art. 50 LINDB — motivacao). reason obrigatorio em
     * PARTIALLY_QUALIFIED e DISQUALIFIED. Apos persistir, o backend
     * dispara mensagem 'Sistema' no chat oficial.
     */
    async updateJudgment(proposalId: string, payload: {
        status: 'QUALIFIED' | 'PARTIALLY_QUALIFIED' | 'DISQUALIFIED';
        reason?: string;
        /**
         * 29/09 (Flavio): ids de opd-uploadeddocuments com o documento que
         * motiva a decisao. Opcional nos tres status.
         */
        documentIds?: string[];
    }): Promise<{ qualificationStatus: string; qualificationReason: string | null; qualifiedAt: string }> {
        const raw = await http.patch(`/proposals/${proposalId}/judgment`, payload);
        const data = (raw as any)?.data ?? raw;
        return {
            qualificationStatus: (data as any)?.qualificationStatus,
            qualificationReason: (data as any)?.qualificationReason ?? null,
            qualifiedAt: (data as any)?.qualifiedAt,
        };
    }

    /**
     * Agenda a decisao de habilitacao pra data/hora futura (Flavio, 13/10/2026).
     * `scheduledFor` em ISO 8601, minimo 1min no futuro. O fornecedor NAO e
     * avisado do agendamento — ele so sabe quando o ato acontece.
     */
    async scheduleJudgment(proposalId: string, payload: {
        scheduledFor: string;
        status: 'QUALIFIED' | 'PARTIALLY_QUALIFIED' | 'DISQUALIFIED';
        reason?: string;
        documentIds?: string[];
        /**
         * No rito comum "Habilitado" e habilitar + declarar vencedor, um ato so
         * (Flavio, 26/06). Quem sabe se este caso e aquele e a TELA, que ja
         * decide isso no onClick do botao. A intencao viaja junto pra execucao
         * agendada fazer o mesmo que o clique faria.
         */
        declareWinner?: boolean;
        awardeeId?: string;
    }): Promise<{ scheduledFor: string; status: string }> {
        const raw = await http.post(`/proposals/${proposalId}/schedule-judgment`, payload);
        const data = (raw as any)?.data ?? raw;
        return {
            scheduledFor: (data as any)?.scheduledFor,
            status: (data as any)?.status,
        };
    }

    /** Cancela o agendamento da decisao. Nada e decidido. Idempotente. */
    async cancelJudgmentSchedule(proposalId: string): Promise<void> {
        await http.delete(`/proposals/${proposalId}/schedule-judgment`);
    }

    /**
     * Adiciona ou remove anexos da decisao de habilitacao JA tomada
     * (Flavio, 29/09). Remover EXIGE motivo — e remover nao apaga: o id fica no
     * changeHistory e o arquivo segue baixavel pelo historico.
     */
    async updateJudgmentDocuments(proposalId: string, payload: {
        add?: string[];
        remove?: string[];
        reason?: string;
    }): Promise<{ qualificationDocumentIds: string[] }> {
        const raw = await http.patch(`/proposals/${proposalId}/judgment/documents`, payload);
        const data = (raw as any)?.data ?? raw;
        return { qualificationDocumentIds: (data as any)?.qualificationDocumentIds ?? [] };
    }

    /**
     * Agente de contratacao anexa documento de habilitacao DO FORNECEDOR obtido
     * em cadastro publico (SICAF, CND, FGTS, CNDT) quando o fornecedor nao
     * apresentou (Flavio, 01/10). `reason` e OBRIGATORIO tambem na inclusao —
     * e a origem do documento (>= 20 chars). Escopo por lote.
     *
     * Nao confundir com updateJudgmentDocuments (anexos que motivam a DECISAO)
     * nem com a diligencia do Art. 64, que segue exclusiva do fornecedor.
     */
    async updateExternalDocuments(proposalId: string, payload: {
        add?: string[];
        remove?: string[];
        reason: string;
    }): Promise<{ externalDocumentIds: string[] }> {
        const raw = await http.patch(`/proposals/${proposalId}/external-documents`, payload);
        const data = (raw as any)?.data ?? raw;
        return { externalDocumentIds: (data as any)?.externalDocumentIds ?? [] };
    }

    /**
     * Reordena manualmente a classificacao de um lote (pregoeiro). Persiste
     * finalRanking na ordem enviada — prevalece sobre a ordem automatica por valor
     * no relatorio de classificacao e na Ata. Justificativa obrigatoria (>= 20 chars).
     */
    async reorderClassification(payload: {
        lotId: string;
        orderedSupplierIds: string[];
        reason: string;
    }): Promise<{ count: number }> {
        const raw = await http.patch('/proposals/reorder-classification', payload);
        const data = (raw as any)?.data ?? raw;
        return { count: Number((data as any)?.count ?? 0) };
    }

    /**
     * Atribui a NOTA TÉCNICA (0-100) de uma proposta em licitacoes por Técnica /
     * Técnica e Preço (Art. 36-37). Backend recusa se o critério não for técnico.
     */
    async setTechnicalScore(proposalId: string, payload: {
        score: number;
        reason?: string;
    }): Promise<{ technicalScore: number }> {
        const raw = await http.patch(`/proposals/${proposalId}/technical-score`, payload);
        const data = (raw as any)?.data ?? raw;
        return { technicalScore: Number((data as any)?.technicalScore ?? payload.score) };
    }
}

export const proposalsService = new ProposalsService();

// Simple form service
const toIsoString = (value: unknown): string | undefined => {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
    }
    if (typeof value !== 'string' || value.trim() === '') return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString();
};

const toNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
};

const toBoolean = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        if (value === 'true') return true;
        if (value === 'false') return false;
    }
    return undefined;
};

const toOptionalString = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
};

const normalizeProposalPayload = (formData: CreateProposalsDto | UpdateProposalsDto) => {
    const payload: Record<string, unknown> = { ...formData };

    const numericFields = ['value', 'percentageOfEstimate', 'referenceValue', 'discountPercentage', 'savingsAmount', 'ranking', 'technicalScore', 'economicScore', 'totalScore', 'deliveryPeriod', 'warrantyPeriod', 'version'];
    numericFields.forEach((field) => {
        if (field in payload) {
            const numericValue = toNumber(payload[field]);
            if (numericValue === undefined) {
                delete payload[field];
            } else {
                payload[field] = numericValue;
            }
        }
    });

    const dateFields = ['submittedAt', 'envelopeSealedAt', 'envelopeOpenedAt', 'validatedAt', 'clarificationDeadline', 'proposalExpiryDate', 'winnerDeclaredAt'];
    dateFields.forEach((field) => {
        if (field in payload) {
            const iso = toIsoString(payload[field]);
            if (!iso) delete payload[field];
            else payload[field] = iso;
        }
    });

    const booleanFields = ['envelopeSealed', 'automaticValidationDone', 'feasibilityAnalysisDone', 'complianceWithRules', 'isWinner', 'active'];
    booleanFields.forEach((field) => {
        if (field in payload) {
            const boolValue = toBoolean(payload[field]);
            if (boolValue === undefined) delete payload[field];
            else payload[field] = boolValue;
        }
    });

    const optionalStringFields = ['lotAuctionSessionId', 'description', 'paymentTerms', 'generalNotes'];
    optionalStringFields.forEach((field) => {
        if (field in payload) {
            const stringValue = toOptionalString(payload[field]);
            if (!stringValue) delete payload[field];
            else payload[field] = stringValue;
        }
    });

    return payload as CreateProposalsDto | UpdateProposalsDto;
};

export const createProposalsFormService = () =>
    new BaseFormService<ProposalsRecord, CreateProposalsDto>({
        basePath: '/proposals',
        initialData: {
            tenderId: '',
            lotId: '',
            supplierId: '',
            lotAuctionSessionId: '',
            proposalNumber: '',
            value: 0,
            judgmentCriteria: 'LOWEST_PRICE',
            currency: 'BRL',
            status: 'draft',
            validationResult: 'pending',
            qualificationStatus: 'PENDING',
            version: 1,
            envelopeSealed: false,
            automaticValidationDone: false,
            complianceWithRules: true,
            isWinner: false,
            active: true,
        },
        validationRules: {
            tenderId: [ValidationRules.required('Tender ID is required')],
            lotId: [ValidationRules.required('Lot ID is required')],
            supplierId: [ValidationRules.required('Supplier ID is required')],
            proposalNumber: [
                ValidationRules.required('Proposal number is required'),
                ValidationRules.minLength(3, 'Proposal number must be at least 3 characters'),
            ],
            value: [
                ValidationRules.required('Value is required'),
                ValidationRules.min(0, 'Value must be positive'),
            ],
            judgmentCriteria: [ValidationRules.required('Judgment criteria is required')],
        },
        normalizeRecord,
        transformForCreate: (formData: CreateProposalsDto) => normalizeProposalPayload(formData) as CreateProposalsDto,
        transformForUpdate: (formData: UpdateProposalsDto) => normalizeProposalPayload(formData) as UpdateProposalsDto,
        transformToForm: (record) => ({
            ...record,
            submittedAt: DateTimeUtils.toDateTimeInputValue(record.submittedAt),
            envelopeSealedAt: DateTimeUtils.toDateTimeInputValue(record.envelopeSealedAt),
            envelopeOpenedAt: DateTimeUtils.toDateTimeInputValue(record.envelopeOpenedAt),
            validatedAt: DateTimeUtils.toDateTimeInputValue(record.validatedAt),
            clarificationDeadline: DateTimeUtils.toDateTimeInputValue(record.clarificationDeadline),
            proposalExpiryDate: DateTimeUtils.toDateTimeInputValue(record.proposalExpiryDate),
            winnerDeclaredAt: DateTimeUtils.toDateTimeInputValue(record.winnerDeclaredAt),
        }),
    });
